import { values } from "lodash";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CannedResponse, CheckCircleFilled, FileDownload, People, Tag } from "@livechat/design-system-icons";
import { DesignToken, Icon, Loader, Text } from "@livechat/design-system-react-components";

import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getSubscriptionPhase } from "helpers/subscription";
import { getNonViewersCount } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import {
  ILiveChatImportDiff,
  IntegrationsActions,
  selectIsManagedByShopify,
  selectIsShopifyTrial,
  selectLiveChatFullImportInProgress,
  selectLiveChatSelectiveImport,
  selectLiveChatSelectiveImportDiff,
} from "store/entities/integrations";
import { selectManagedByPartner } from "store/entities/license";
import { selectCurrentSubscription, selectSeatsCount } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { LiveChatDataImportType } from "../../constants";

import { AgentsAndGroupsImportModal } from "./AgentsAndGroupsImportModal";
import { ImportItem } from "./ImportItem";

type SelectiveImportStatus = "importing" | "imported" | "unimported" | "checking";

const mapImportTypeToLoadingMessage = (importType: LiveChatDataImportType): string | null => {
  switch (importType) {
    case LiveChatDataImportType.AgentsAndGroups:
      return "agents and groups";
    case LiveChatDataImportType.CannedResponses:
      return "shared canned responses";
    case LiveChatDataImportType.Tags:
      return "tags";
    default:
      return null;
  }
};

const ImportStatus: FC<{ status: SelectiveImportStatus; message?: string | null }> = ({ status, message }) => {
  switch (status) {
    case "importing":
      return <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Importing...</Text>;
    case "imported":
      return (
        <>
          <Icon source={CheckCircleFilled} kind="success" size="small" />
          <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>{message || "All imported"}</Text>
        </>
      );
    case "unimported":
      return (
        <>
          <Icon source={FileDownload} kind="action-warning" size="small" />
          <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Unimported: {message}</Text>
        </>
      );
    case "checking":
      return (
        <>
          <Loader size="small" />
          <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>Checking for unimported {message}…</Text>
        </>
      );
    default:
      return null;
  }
};

const mapImportDiffToContentMessageProps = (
  importType: LiveChatDataImportType,
  fullImportInProgress: boolean,
  importDiff?: ILiveChatImportDiff
): { status: SelectiveImportStatus; message?: string | null } => {
  if (fullImportInProgress) {
    return {
      status: "importing",
    };
  }

  if (!importDiff) {
    return {
      status: "checking",
      message: mapImportTypeToLoadingMessage(importType),
    };
  }

  switch (importType) {
    case LiveChatDataImportType.AgentsAndGroups:
      if (importDiff.agents.status === "complete" && importDiff.groups.status === "complete") {
        return {
          status: "imported",
        };
      }
      if (importDiff.agents.status === "pending" || importDiff.groups.status === "pending") {
        return {
          status: "importing",
        };
      }
      return {
        status: "unimported",
        message: `${importDiff.agents.create} ${plural(importDiff.agents.create, "agent", "agents")} and ${
          importDiff.groups.create
        } ${plural(importDiff.groups.create, "group", "groups")}`,
      };

    case LiveChatDataImportType.CannedResponses:
      if (importDiff.cannedResponses.status === "complete") {
        return {
          status: "imported",
          message: "All shared imported",
        };
      }
      if (importDiff.cannedResponses.status === "pending") {
        return {
          status: "importing",
        };
      }
      return {
        status: "unimported",
        message: `${importDiff.cannedResponses.create} ${plural(
          importDiff.cannedResponses.create,
          "shared canned response",
          "shared canned responses"
        )}`,
      };

    case LiveChatDataImportType.Tags:
      if (importDiff.tags.status === "complete") {
        return {
          status: "imported",
        };
      }
      if (importDiff.tags.status === "pending") {
        return {
          status: "importing",
        };
      }
      return {
        status: "unimported",
        message: `${importDiff.tags.create} ${plural(importDiff.tags.create, "tag", "tags")}`,
      };
    default:
      return {
        status: "checking",
        message: mapImportTypeToLoadingMessage(importType),
      };
  }
};

export const SelectiveImportItems: FC = () => {
  const dispatch = useDispatch();
  const importDiff = useSelector(selectLiveChatSelectiveImportDiff);
  const selectiveImport = useSelector(selectLiveChatSelectiveImport);
  const fullImportInProgress = useSelector(selectLiveChatFullImportInProgress);
  const billing = useSelector(getBilling);
  const subscription = useSelector(selectCurrentSubscription);
  const seatsCount = useSelector(selectSeatsCount);
  const nonViewersCount = useSelector(getNonViewersCount);
  const isHandledByShopify = useSelector(selectIsManagedByShopify);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);
  const isManagedByPartner = useSelector(selectManagedByPartner);

  const subscriptionPhase = getSubscriptionPhase(subscription, billing);
  const agentsToImport = importDiff?.agents?.create || 0;
  const seatsToAdd = agentsToImport - (seatsCount - nonViewersCount);

  const [displayAgentsAndGroupsImportModal, setDisplayAgentsAndGroupsImportModal] = useState(false);

  const items = {
    [LiveChatDataImportType.AgentsAndGroups]: {
      id: LiveChatDataImportType.AgentsAndGroups,
      title: "Agents and groups",
      icon: People,
      ...mapImportDiffToContentMessageProps(LiveChatDataImportType.AgentsAndGroups, fullImportInProgress, importDiff),
    },
    [LiveChatDataImportType.CannedResponses]: {
      id: LiveChatDataImportType.CannedResponses,
      title: "Canned responses",
      icon: CannedResponse,
      ...mapImportDiffToContentMessageProps(LiveChatDataImportType.CannedResponses, fullImportInProgress, importDiff),
    },
    [LiveChatDataImportType.Tags]: {
      id: LiveChatDataImportType.Tags,
      title: "Tags",
      icon: Tag,
      ...mapImportDiffToContentMessageProps(LiveChatDataImportType.Tags, fullImportInProgress, importDiff),
    },
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleStartSelectiveImport = (type: LiveChatDataImportType) => {
    trackEngagement("LC integration: Import data", EventPlace.Settings, {
      importedData: type,
      firstTime:
        selectiveImport &&
        selectiveImport[type as Exclude<LiveChatDataImportType, LiveChatDataImportType.Tickets>].status ===
          "notPerformed",
    });
    dispatch(
      IntegrationsActions.startLiveChatSelectiveImport({ types: [type as Exclude<LiveChatDataImportType, "tickets">] })
    );
  };

  const handleOpenAgentsAndGroupsImportModal = (): void => {
    setDisplayAgentsAndGroupsImportModal(true);
  };

  const handleCloseAgentsAndGroupsImportModal = (): void => {
    setDisplayAgentsAndGroupsImportModal(false);
  };

  const handleImportClick = (type: LiveChatDataImportType): void => {
    // Show disclaimer modal if the user is on a paid plan, has agents to import and needs to add seats to the subscription
    const shouldShowDisclaimer =
      (subscriptionPhase === SubscriptionPhase.Paid ||
        subscriptionPhase === SubscriptionPhase.PaidNoCC ||
        (isHandledByShopify && !isShopifyTrial) ||
        isManagedByPartner) &&
      type === LiveChatDataImportType.AgentsAndGroups &&
      agentsToImport > 0 &&
      seatsToAdd > 0;

    if (shouldShowDisclaimer) {
      handleOpenAgentsAndGroupsImportModal();
    } else {
      handleStartSelectiveImport(type);
    }
  };

  const handleAgentsAndGroupsImportStart = (): void => {
    handleStartSelectiveImport(LiveChatDataImportType.AgentsAndGroups);
    handleCloseAgentsAndGroupsImportModal();
  };

  return (
    <>
      {values(items).map(item => (
        <ImportItem
          key={item.id}
          title={item.title}
          icon={item.icon}
          status={<ImportStatus status={item.status} message={item.message} />}
          disabled={item.status === "checking" || item.status === "imported"}
          loading={item.status === "importing"}
          onImportClick={() => handleImportClick(item.id)}
        />
      ))}
      {displayAgentsAndGroupsImportModal && (
        <AgentsAndGroupsImportModal
          onClose={handleCloseAgentsAndGroupsImportModal}
          onImportStart={handleAgentsAndGroupsImportStart}
        />
      )}
    </>
  );
};
